import { Fragment, useMemo } from 'react';
import { Add, Close } from '@mui/icons-material';
import { Button, FormControl, FormHelperText, Grid, MenuItem, TextField, Box } from '@mui/material';
import { FieldArray } from 'formik';
import { formatInputPhoneNumber } from '../../../helpers';
import { useQuery } from 'react-query';
import { IDropdownResponse, IUserType, userTypes } from '../../../models';
import { SMBSelect } from '../../../components';
import { getCommonLookupVendors } from '../../../fetch/lookups';

export const VendorAffiliateFields = ({
  values,
  isFormDisabled,
  handleBlur,
  setFieldValue,
  errors,
  touched,
  showContactInfo = true
}) => {
  const { isLoading: isLoadingVendors, data: vendorsData } = useQuery<IDropdownResponse[], Error>(
    ['vendors'],
    () => getCommonLookupVendors()
  );

  const vendors = useMemo(() => vendorsData ?? [], [vendorsData]);

  return (
    <>
      {showContactInfo && (
        <>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              variant="outlined"
              autoComplete="nope"
              label="First Name"
              name="firstName"
              value={values.firstName}
              disabled={isFormDisabled}
              onBlur={handleBlur}
              size="small"
              inputProps={{
                'data-lpignore': 'true'
              }}
              error={touched.firstName && errors && errors.firstName ? true : false}
              helperText={touched.firstName && errors && errors.firstName}
              onChange={e => setFieldValue('firstName', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              variant="outlined"
              autoComplete="nope"
              label="Last Name"
              name="lastName"
              inputProps={{
                'data-lpignore': 'true'
              }}
              value={values.lastName}
              disabled={isFormDisabled}
              onBlur={handleBlur}
              size="small"
              error={touched.lastName && errors && errors.lastName ? true : false}
              helperText={touched.lastName && errors && errors.lastName}
              onChange={e => setFieldValue('lastName', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              onChange={e => setFieldValue('email', e.target.value)}
              error={touched.email && errors && errors.email ? true : false}
              helperText={touched.email && errors && errors.email}
              fullWidth={true}
              id="email"
              label="Email (Username)"
              size="small"
              name="email"
              onBlur={handleBlur}
              required={true}
              autoComplete="off"
              value={values.email}
              disabled={isFormDisabled}
              inputProps={{
                'data-lpignore': true // Last Pass disable auto-fill
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              variant="outlined"
              autoComplete="nope"
              label="Phone Number"
              name="phone"
              value={values.phoneNumber}
              disabled={isFormDisabled}
              onBlur={handleBlur}
              size="small"
              onChange={e => {
                setFieldValue('phoneNumber', formatInputPhoneNumber(e.target.value));
              }}
              error={Boolean(touched.phoneNumber && errors.phoneNumber)}
              helperText={touched.phoneNumber && errors.phoneNumber}
            />
          </Grid>
        </>
      )}
      <Grid item xs={12}>
        <FieldArray name="partners">
          {({ push, remove }) => {
            return (
              <>
                <Grid container spacing={1} alignItems="flex-start">
                  {values.partners.map((part, index) => {
                    return (
                      <Fragment key={`${index}`}>
                        <Grid item xs={12} sm={5}>
                          <FormControl
                            required
                            fullWidth
                            error={!part?.partnerId}
                            variant="outlined"
                            size="small"
                          >
                            <SMBSelect
                              name={`vendors`}
                              id={`vendors`}
                              size="small"
                              label="Vendor"
                              error={!part?.partnerId}
                              required
                              variant="outlined"
                              value={part.partnerId}
                              handleClear={() => setFieldValue(`partners.${index}.partnerId`, '')}
                              onBlur={handleBlur}
                              disabled={isLoadingVendors || isFormDisabled}
                              onChange={e =>
                                setFieldValue(`partners.${index}.partnerId`, e.target.value)
                              }
                              SelectProps={{
                                MenuProps: {
                                  sx: {
                                    maxHeight: '30rem'
                                  }
                                }
                              }}
                            >
                              {vendors &&
                                vendors.map((vendor, vendorIndex) => {
                                  return (
                                    <MenuItem key={`${vendorIndex}`} value={vendor.value}>
                                      {vendor.description}
                                    </MenuItem>
                                  );
                                })}
                            </SMBSelect>
                            {!part?.partnerId && <FormHelperText error>Required</FormHelperText>}
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={5}>
                          <SMBSelect
                            variant="outlined"
                            onChange={e => {
                              setFieldValue(
                                `partners.${index}.roles`,
                                typeof e.target.value === 'string'
                                  ? e.target.value.split(',')
                                  : e.target.value
                              );
                            }}
                            SelectProps={{
                              multiple: true
                            }}
                            handleClear={() => {
                              setFieldValue(`partners.${index}.roles`, []);
                            }}
                            required
                            name={`roles`}
                            id={`roles`}
                            label="User Type"
                            size="small"
                            value={part.roles}
                            error={part?.roles?.length === 0}
                            helperText={part?.roles?.length === 0 ? 'Required' : ''}
                            disabled={isFormDisabled}
                          >
                            {userTypes.map((type: IUserType, index) => {
                              return (
                                <MenuItem key={`${index + 1}`} value={type.value}>
                                  {type.name}
                                </MenuItem>
                              );
                            })}
                          </SMBSelect>
                        </Grid>
                        {values.partners?.length > 0 && (
                          <Grid item xs={12} sm={2}>
                            <Button
                              id="remove-vendor"
                              startIcon={<Close />}
                              variant="text"
                              color="error"
                              onClick={() => {
                                remove(index);
                              }}
                            >
                              Remove
                            </Button>
                          </Grid>
                        )}
                      </Fragment>
                    );
                  })}
                </Grid>
                <Box mt={1}>
                  <Button
                    id="add-vendor"
                    color="primary"
                    variant="text"
                    startIcon={<Add />}
                    onClick={() =>
                      push({
                        partnerId: '',
                        roles: []
                      })
                    }
                  >
                    Add Vendor
                  </Button>
                </Box>
              </>
            );
          }}
        </FieldArray>
      </Grid>
    </>
  );
};
